import React, { useCallback } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Checkbox, Chip, IconButton, Stack, Typography } from '@mui/material';

import { ConflictResolution, ManualRoom } from '~/types/stopsell';

import { countNights, formatDateRange } from '~/utils/stopsellDateUtils';
import { pluralizeToString } from '~/utils/stringUtils';

import StopsellConflictBanner from './StopsellConflictBanner';
import StopsellResolvedBanner from './StopsellResolvedBanner';

interface Props {
  date: ManualRoom['dates'][number];
  dateIndex: number;
  onRemoveDate: (dateIndex: number) => void;
  onToggleDate: (dateIndex: number, selected: boolean) => void;
  onResolve: (dateIndex: number, conflictIndex: number, status: ConflictResolution) => void;
  onUndo: (dateIndex: number, conflictIndex: number) => void;
}

export default function StopsellManualRoomDateRow({
  date,
  dateIndex,
  onRemoveDate,
  onToggleDate,
  onResolve,
  onUndo,
}: Props) {
  const handleRemove = useCallback(() => onRemoveDate(dateIndex), [onRemoveDate, dateIndex]);
  const handleToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onToggleDate(dateIndex, e.target.checked),
    [onToggleDate, dateIndex],
  );

  const isDisabled = date.conflicts.some((_, i) => !date.resolutions[i] || date.resolutions[i] === 'ignored');
  const isIgnored = date.conflicts.length > 0 && date.resolutions.some((r) => r === 'ignored');

  const rangesToMerge = [
    { start: date.start, end: date.end },
    ...date.conflicts.filter((_, i) => date.resolutions[i] === 'extending').map((c) => c.existingRange),
  ];
  const displayDate =
    rangesToMerge.length > 1
      ? {
          start: rangesToMerge.reduce((min, r) => (r.start < min ? r.start : min), date.start),
          end: rangesToMerge.reduce((max, r) => (r.end > max ? r.end : max), date.end),
        }
      : { start: date.start, end: date.end };

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Checkbox size="small" checked={date.selected} onChange={handleToggle} disabled={isDisabled} />
        <Typography
          variant="body2"
          color={isIgnored ? 'text.disabled' : 'text.secondary'}
          sx={isIgnored ? { textDecoration: 'line-through' } : undefined}
        >
          {formatDateRange(displayDate.start, displayDate.end)}
        </Typography>
        <Chip label={pluralizeToString('night', countNights(displayDate.start, displayDate.end))} size="small" />
        <IconButton size="small" onClick={handleRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      {date.conflicts.map((conflict, conflictIndex) => {
        const resolution = date.resolutions[conflictIndex] ?? null;
        return resolution ? (
          <StopsellResolvedBanner
            key={conflictIndex}
            conflict={conflict}
            resolution={resolution}
            onUndo={() => onUndo(dateIndex, conflictIndex)}
          />
        ) : (
          <StopsellConflictBanner
            key={conflictIndex}
            conflict={conflict}
            onResolve={(status) => onResolve(dateIndex, conflictIndex, status)}
          />
        );
      })}
    </Stack>
  );
}
